///
/// EarthBrowser
///
/// Created by Matt Giger on 3/18/2024
/// Copyright (c) 2024 Geodata Labs
///

import type { EarthquakeProperties } from "@/earthbrowser/dataset-distribution/earthquakes/EarthquakesTable"
import { QuakeMarkerNode, quakeTimeFade, type QuakeMarkerStyle } from "@/earthbrowser/dataset-distribution/earthquakes/QuakeMarkerNode"
import { EntityCameraAdapter } from "@/earthbrowser/datasets/EntityCameraAdapter"
import { type HoverHighlightTarget } from "@/earthbrowser/datasets/EntityHoverHandler"
import { type InfoPanelData, type InfoPanelProvider } from "@/earthbrowser/datasets/InfoPanelProvider"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { geographic } from "@/earthbrowser/utils/geographic"
import { pushTargetToHistory } from "@/utils/navigationHistory"
import type { Camera } from "@stovokor/camera/Camera"
import { type RenderActionSet } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type FocusTransitionConfig } from "@stovokor/gestures/FocusTarget"
import { GestureNode } from "@stovokor/gestures/GestureNode"
import { type GestureHitResult } from "@stovokor/gestures/GestureRecognizer"
import { dot, vec3 } from "@stovokor/math"
import { trig } from "@stovokor/math/math"
import { matrix } from "@stovokor/math/matrix"
import { type ray } from "@stovokor/math/ray"
import { type DatasetSceneObject } from "../DatasetLayer"

const cQuakeModelAltitude	= 0.5
const cQuakeFocusDistance	= 10
const cQuakeFocusMaxZoomRadius = 100

// Pointer-target floor in radians of apparent radius (~4px on a 1080p
// screen). Static quakes render as ~1px flecks; a hit area that small is
// pixel hunting, so the pick disc is inflated past the drawn dome while
// the rendering stays unchanged
const cPickMinAngularSize = 0.003

/**
 * One quake's answer to "is the pointer over you, and how squarely":
 * the engine hit plus the pointer's offset from the dome center as a
 * fraction of the dome's apparent radius (0 = dead center, 1 = rim) and
 * the magnitude, which the layer ranks across the cluster under the cursor
 */
export interface QuakePickCandidate {
	feature: QuakeFeature
	hit: GestureHitResult
	offset: number
	mag: number
}

export class QuakeFeature extends GestureNode implements DatasetSceneObject<EarthquakeProperties>, InfoPanelProvider, HoverHighlightTarget {
	public readonly focusable: boolean = true
	public readonly id: string

	public feature: EarthquakeProperties
	public readonly coordinate: geographic

	private readonly cameraAdapter: EntityCameraAdapter
	private readonly featureContext: EarthBrowserContext

	private readonly marker: QuakeMarkerNode
	private readonly modelRotation = matrix.identity

	private eventTimeMs = 0
	private focused = false
	/** Clock-driven opacity; 0 means hidden (before the event or fully faded) */
	private timeFade = 1
	/** Faded down while a cutout block is open over this quake's location */
	private dimmed = false

	constructor(context: EarthBrowserContext, feature: EarthquakeProperties) {
		super(context)
		this.id = feature.id
		this.feature = feature
		this.featureContext = context

		const geo = new geographic(
			this.feature.lon * trig.rad,
			this.feature.lat * trig.rad,
			cQuakeModelAltitude
		)
		this.coordinate = geo

		const xRot = matrix.axisRotation(vec3.unitX, this.coordinate.latitude + trig.half_pi)
		const yRot = matrix.axisRotation(vec3.unitY, this.coordinate.longitude + trig.pi)
		this.modelRotation = yRot.premultiply(xRot)

		this.transform = matrix.translate(geo.cartesian)
			.premultiply(this.modelRotation)

		this.marker = new QuakeMarkerNode(context, this.markerStyle())
		this.addNode(this.marker)

		// Bigger quakes need a farther focus distance so the camera stays outside the dome
		const focusDistance = Math.max(cQuakeFocusDistance, this.marker.radius * 3)
		const maxZoomRadius = Math.max(cQuakeFocusMaxZoomRadius, this.marker.radius * 10)
		this.cameraAdapter = new EntityCameraAdapter(context, this, focusDistance, maxZoomRadius)
	}

	private markerStyle(): QuakeMarkerStyle {
		const eventTimeMs = Date.parse(this.feature.event_time)
		this.eventTimeMs = Number.isNaN(eventTimeMs) ? 0 : eventTimeMs
		return {
			mag: this.feature.mag,
			eventTimeMs: this.eventTimeMs,
			alert: this.feature.alert,
			tsunami: this.feature.tsunami === 1
		}
	}

	/**
	 * Update the feature data for this scene object. Called on every dataset
	 * poll (~1 min); recency animation and fade follow the clock per frame.
	 */
	public updateFeature(feature: EarthquakeProperties): void {
		this.feature = feature
		this.marker.updateStyle(this.markerStyle())
	}

	/**
	 * Visibility follows the simulation clock: hidden before the event time
	 * (scrubbing the timeline replays quakes as they struck), then fading out
	 * over a magnitude-scaled lifetime so big quakes linger for days while
	 * minor ones clear within hours. A focused quake stays visible so the
	 * info panel never points at nothing.
	 */
	override render(state: RenderState): RenderActionSet[] {
		const clockMs = this.featureContext.clock.clockTime.date.getTime()
		this.timeFade = this.focused ? 1 : quakeTimeFade(this.feature.mag, clockMs - this.eventTimeMs)
		if(this.timeFade <= 0) {
			return []
		}
		this.marker.updateClock(clockMs)
		this.marker.setTimeFade(this.dimmed ? this.timeFade * 0.12 : this.timeFade)
		return super.render(state)
	}

	/**
	 * While the cutout block hovers over this quake, the surface dome
	 * underneath it is noise: the hypocenter is drawn in the block instead
	 */
	public setDimmed(dimmed: boolean): void {
		this.dimmed = dimmed
	}

	/** Hover cue from EntityHoverHandler: show which quake a tap would pick */
	public onHoverHighlight(hovered: boolean): void {
		this.marker.setHovered(hovered)
	}

	// ========== InfoPanelProvider ==========

	public getInfoPanelData(): InfoPanelData {
		return {
			title: this.feature.place ?? String(this.feature.id ?? "Earthquake"),
			theme: "earthquake",
			contentType: "earthquake",
			contentData: {
				type: "earthquake",
				earthquake: this.feature
			}
		}
	}

	// ========== Focus Lifecycle ==========

	public onFocusGained(camera: Camera): void {
		this.focused = true
		this.cameraAdapter.attach(camera)
		this.marker.setFocused(true)

		// Push to browser history for back/forward navigation
		const quakeId = this.feature.id
		if (quakeId) {
			pushTargetToHistory('quake', String(quakeId))
		}
	}

	public onFocusLost(): void {
		this.focused = false
		this.cameraAdapter.detach()
		this.marker.setFocused(false)
	}

	public onFocusUpdate(): void {
		this.cameraAdapter.update()
	}

	public focusTransition(camera: Camera): FocusTransitionConfig {
		return {
			target: () => this.cameraAdapter.getTargetCameraState(camera),
			params: {
				duration: 1,
				easing: "easeInOut"
			}
		}
	}

	// ========== Command Handlers ==========

	public handleCommand(command: string, data?: unknown): boolean {
		// Delegate camera and compass commands to the adapter
		return this.cameraAdapter.handleCommand(command, data)
	}

	/**
	 * Pointer test in the parent layer's space (the layer calls this for
	 * every quake and arbitrates the cluster itself, so overlapping domes
	 * don't race on raw ray distance). `inflate` widens the pick disc, used
	 * by the layer to keep the current pick sticky near its rim
	 */
	public pickCandidate(pointer: ray, cameraPosition: vec3, inflate = 1): QuakePickCandidate | undefined {
		const center = this.transform.position
		return this.intersectDisc(pointer, center, cameraPosition.subtract(center).length, inflate)
	}

	override gestureIntersection(state: RenderState): GestureHitResult | undefined {
		// Local space: the dome is centered on the origin
		const cameraDistance = state.camera.position.subtract(this.transform.position).length
		return this.intersectDisc(state.ray, vec3.zero, cameraDistance)?.hit
	}

	private intersectDisc(pointer: ray, center: vec3, cameraDistance: number, inflate = 1): QuakePickCandidate | undefined {
		// Faded-out quakes aren't rendered, so they shouldn't be clickable
		if(this.timeFade <= 0) {
			return undefined
		}

		const toCenter = center.subtract(pointer.origin)
		const along = dot(toCenter, pointer.direction)
		if(along <= 0) {
			return undefined		// behind the pointer
		}

		// Inflate the hit sphere the same way the marker inflates on screen,
		// so far-away markers rendered at their minimum apparent size stay
		// clickable, with a floor so static flecks are still a usable target
		const angularSize = Math.max(this.marker.minAngularSize, cPickMinAngularSize)
		const radius = Math.max(this.marker.radius, cameraDistance * angularSize) * inflate

		const perpSq = Math.max(0, dot(toCenter, toCenter) - along * along)
		if(perpSq > radius * radius) {
			return undefined
		}

		// Entry point on the sphere, matching what the engine's ray-vs-bounds test reports
		const distance = Math.max(0, along - Math.sqrt(radius * radius - perpSq))
		return {
			feature: this,
			hit: { node: this, hitPoint: pointer.pointAt(distance), distance },
			offset: Math.sqrt(perpSq) / radius,
			mag: this.feature.mag
		}
	}
}
